import { Button } from '../ui/Button'
import { Input } from '../ui/Input'
import type { BulkProductFailure } from '../../lib/bulkProductOperations'
import type { CategoryRow } from '../../services/categoryService'

type ProductBulkActionsBarProps = {
  selectedCount: number
  busy: boolean
  categoryRows: CategoryRow[]
  bulkCategorySlug: string
  onBulkCategorySlugChange: (slug: string) => void
  bulkPrice: string
  onBulkPriceChange: (value: string) => void
  bulkStock: string
  onBulkStockChange: (value: string) => void
  failures: BulkProductFailure[]
  onPublish: () => void
  onUnpublish: () => void
  onAssignCategory: () => void
  onApplyPriceStock: () => void
  onDelete: () => void
  onClearSelection: () => void
}

export function ProductBulkActionsBar({
  selectedCount,
  busy,
  categoryRows,
  bulkCategorySlug,
  onBulkCategorySlugChange,
  bulkPrice,
  onBulkPriceChange,
  bulkStock,
  onBulkStockChange,
  failures,
  onPublish,
  onUnpublish,
  onAssignCategory,
  onApplyPriceStock,
  onDelete,
  onClearSelection,
}: ProductBulkActionsBarProps) {
  if (selectedCount === 0) return null

  const canApplyPriceStock = bulkPrice.trim() !== '' || bulkStock.trim() !== ''

  return (
    <div className="mb-4 space-y-3 rounded-xl border border-primary/25 bg-primary/5 p-4">
      <div className="flex flex-col gap-3 lg:flex-row lg:flex-wrap lg:items-center lg:justify-between">
        <p className="text-sm font-medium text-foreground">
          <span className="tabular-nums">{selectedCount}</span> selected on this page
        </p>
        <div className="flex flex-wrap items-end gap-2">
          <Button type="button" size="sm" disabled={busy} onClick={onPublish}>
            Publish
          </Button>
          <Button type="button" size="sm" variant="secondary" disabled={busy} onClick={onUnpublish}>
            Unpublish
          </Button>
          <div className="flex flex-wrap items-end gap-2">
            <Input
              id="bulk-price"
              label="Price"
              type="number"
              inputMode="decimal"
              min={0.01}
              step={0.01}
              placeholder="Same for all"
              value={bulkPrice}
              disabled={busy}
              className="w-32"
              onChange={(e) => onBulkPriceChange(e.target.value)}
            />
            <Input
              id="bulk-stock"
              label="Stock"
              type="number"
              inputMode="numeric"
              min={0}
              step={1}
              placeholder="Same for all"
              value={bulkStock}
              disabled={busy}
              className="w-28"
              onChange={(e) => onBulkStockChange(e.target.value)}
            />
            <Button
              type="button"
              size="sm"
              variant="secondary"
              disabled={busy || !canApplyPriceStock}
              onClick={onApplyPriceStock}
            >
              Apply price/stock
            </Button>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <select
              className="rounded-lg border border-border bg-card px-2 py-1.5 text-sm text-foreground"
              value={bulkCategorySlug}
              disabled={busy}
              onChange={(e) => onBulkCategorySlugChange(e.target.value)}
              aria-label="Category for bulk assign"
            >
              <option value="">Assign category…</option>
              {categoryRows.map((c) => (
                <option key={`${c.source}-${c.slug}-${c.id ?? 'x'}`} value={c.slug}>
                  {c.name}
                </option>
              ))}
            </select>
            <Button
              type="button"
              size="sm"
              variant="secondary"
              disabled={busy || !bulkCategorySlug}
              onClick={onAssignCategory}
            >
              Apply category
            </Button>
          </div>
          <Button type="button" size="sm" variant="danger" disabled={busy} onClick={onDelete}>
            Delete
          </Button>
          <Button type="button" size="sm" variant="secondary" disabled={busy} onClick={onClearSelection}>
            Clear
          </Button>
        </div>
      </div>
      {failures.length > 0 ? (
        <ul className="space-y-1 rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-xs text-destructive">
          {failures.map((f) => (
            <li key={f.id}>
              <span className="font-medium">{f.label}</span>: {f.message}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  )
}
